import React, { useState, useEffect, useRef, useCallback } from "react";

const GooglePlacesAutocompleteWithPin = ({ placeholder, result, key, value = "", required = false, disabled = false }) => {
  const [searchText, setSearchText] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const debounceTimeout = useRef(null);
  const inputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    setSearchText(value);
  }, [value]);

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      if (!window.google?.maps?.places?.AutocompleteSuggestion) {
        if (window.google?.maps?.importLibrary) {
          await window.google.maps.importLibrary("places");
        }
      }
      const { suggestions: results } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedPrimaryTypes: ["geocode"],
      });

      // Show ALL geocode suggestions — the backend resolves "City, ST" / state /
      // city to a KMA even without a postal code, so we no longer pre-filter.
      const all = (results || []).slice(0, 5).map((s) => ({
        suggestion: s,
        place: s.placePrediction.toPlace(),
      }));
      setSuggestions(all);
    } catch (e) {
      console.warn("AutocompleteSuggestion fetch failed:", e.message);
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (e) => {
    const input = e.target.value;
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setSearchText(input);

    // Notify the parent of every keystroke so it can capture the raw text
    // even when the user submits without picking a Google Places suggestion.
    // Parent decides how to use this (typically as a "City, ST" fallback).
    if (typeof result === "function") {
      result({ event: "typeChange", value: input });
    }

    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(input);
    }, 500);
  };

  const handleSelect = async (item) => {
    try {
      const { suggestion, place } = item;
      // Re-fetch with full fields if needed
      await place.fetchFields({ fields: ["formattedAddress", "addressComponents", "location"] });

      // Convert to legacy format
      const legacyPlace = {
        formatted_address: place.formattedAddress,
        geometry: place.location ? {
          location: {
            lat: () => place.location.lat(),
            lng: () => place.location.lng(),
          }
        } : undefined,
        address_components: (place.addressComponents || []).map(c => ({
          long_name: c.longText || "",
          short_name: c.shortText || "",
          types: c.types || [],
        })),
        place_id: suggestion.placePrediction?.placeId,
      };

      setSearchText(place.formattedAddress || "");
      setSuggestions([]);
      result({ event: "placeChange", value: place.formattedAddress || "", place: legacyPlace });
    } catch (e) {
      console.warn("Place fetch failed:", e.message);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setSuggestions([]), 300);
  };

  return (
    <div>
      <input
        key={key}
        ref={inputRef}
        type="text"
        onBlur={handleBlur}
        placeholder={placeholder || "Enter a location"}
        value={searchText}
        required={required}
        disabled={disabled}
        className="form-control py-4"
        onChange={handleInputChange}
      />
      {suggestions.length > 0 && (
        <ul style={{
          position: "fixed",
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 9999,
          background: "white",
          border: "1px solid #ddd",
          listStyle: "none",
          margin: 0,
          padding: 0,
          maxHeight: "200px",
          overflowY: "auto",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          borderRadius: "0 0 4px 4px",
        }}>
          {suggestions.map((item, i) => (
            <li
              key={item.suggestion.placePrediction?.placeId || i}
              style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(item)}
            >
              <div style={{ fontWeight: 500 }}>{item.suggestion.placePrediction?.mainText?.text || ""}</div>
              <div style={{ fontSize: "12px", color: "#888" }}>{item.suggestion.placePrediction?.secondaryText?.text || ""}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GooglePlacesAutocompleteWithPin;
